import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Schedule from "@/pages/Schedule";
import JobDetail from "@/pages/JobDetail";
import Issues from "@/pages/Issues";
import Team from "@/pages/Team";
import Inspections from "@/pages/Inspections";
import Automations from "@/pages/Automations";
import Invoices from "@/pages/Invoices";
import Finance from "@/pages/Finance";
import Messages from "@/pages/Messages";
import Vendors from "@/pages/Vendors";
import MyPay from "@/pages/MyPay";
import Reports from "@/pages/Reports";
import OperationsMap from "@/pages/Map";
import Branding from "@/components/Branding";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Activity from "@/pages/Activity";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Platform from "@/pages/Platform";
import AcceptInvite from "@/pages/AcceptInvite";
import SubscriptionRequired from "@/components/SubscriptionRequired";
import PublicPay from "@/pages/PublicPay";
import { TermsPage, PrivacyPage } from "@/pages/Legal";
import AdFilmPage from "@/pages/AdFilmPage";
import StormFilmPage from "@/pages/StormFilmPage";
import DisruptorFilmPage from "@/pages/DisruptorFilmPage";
import MasterFilmPage from "@/pages/MasterFilmPage";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
import "@/App.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-royal" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  const org = user.active_organization;
  const unpaid =
    org && !org.card_on_file && (org.plan_status || "pre_trial") === "pre_trial" && !user.platform_role;
  // /payment/* is how the card gets added — gating it would trap the user in a loop.
  if (unpaid && !pathname.startsWith("/payment")) return <SubscriptionRequired />;
  return children;
};

const LoginGate = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Login />;
};

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/pay/:token" element={<PublicPay />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/ad" element={<AdFilmPage />} />
          <Route path="/ad2" element={<StormFilmPage />} />
          <Route path="/ad3" element={<DisruptorFilmPage />} />
          <Route path="/ad4" element={<MasterFilmPage />} />
          <Route
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/map" element={<OperationsMap />} />
            <Route path="/schedule" element={<Schedule />} />
            {/* The work board lives at /schedule; keep the name people actually type working. */}
            <Route path="/work-orders" element={<Navigate to="/schedule" replace />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/issues" element={<Issues />} />
            <Route path="/team" element={<Team />} />
            <Route path="/inspections" element={<Inspections />} />
            <Route path="/automations" element={<Automations />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/my-pay" element={<MyPay />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/platform" element={<Platform />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Branding />
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}
